import type { MissionHeatmapLocationInput } from "./missionHeatmapDisplay";
import {
  ensureRankedHeatmapLocations,
  experienceHeatmapMaxRadiusMiles,
} from "./missionHeatmapExperienceRanking";
import type { ProfileMapFitBounds } from "./ProfileMapLibreSurface";

export type ProfileMapCenter = Readonly<{
  latitude: number;
  longitude: number;
  displayLabel?: string;
}>;

export type MissionHeatmapPointFeatureProperties = Readonly<{
  locationKey: string;
  cityLabel: string;
  latitude: number;
  longitude: number;
  missionCount: number;
  portfolioPercentage: number;
  experiencePercentile: number;
  percentileLabel: string;
  heatLevel: MissionHeatmapLocationInput["heatLevel"];
  heatOpacity: number;
  radiusMiles: number;
}>;

export type MissionHeatmapPointFeature = Readonly<{
  type: "Feature";
  properties: MissionHeatmapPointFeatureProperties;
  geometry: Readonly<{
    type: "Point";
    coordinates: readonly [number, number];
  }>;
}>;

export type MissionHeatmapPointFeatureCollection = Readonly<{
  type: "FeatureCollection";
  features: readonly MissionHeatmapPointFeature[];
}>;

const milesPerDegreeLatitude = 69;

function milesToLongitudeDegrees(miles: number, latitude: number): number {
  const latitudeRadians = (latitude * Math.PI) / 180;
  const milesPerDegreeLongitude = milesPerDegreeLatitude * Math.max(0.2, Math.cos(latitudeRadians));
  return miles / milesPerDegreeLongitude;
}

export function buildMissionHeatmapPointFeatureCollection(
  locations: readonly MissionHeatmapLocationInput[],
): MissionHeatmapPointFeatureCollection {
  const rankedLocations = ensureRankedHeatmapLocations(locations);
  if (rankedLocations.length === 0) {
    return { type: "FeatureCollection", features: [] };
  }

  return {
    type: "FeatureCollection",
    features: rankedLocations.map((location) => ({
      type: "Feature",
      properties: {
        locationKey: location.locationKey,
        cityLabel: location.cityLabel,
        latitude: location.latitude,
        longitude: location.longitude,
        missionCount: location.missionCount,
        portfolioPercentage: location.portfolioPercentage ?? 0,
        experiencePercentile: location.experiencePercentile ?? 0,
        percentileLabel: location.percentileLabel ?? "",
        heatLevel: location.heatLevel ?? "none",
        heatOpacity: location.heatOpacity ?? 0.85,
        radiusMiles: location.radiusMiles ?? experienceHeatmapMaxRadiusMiles,
      },
      geometry: {
        type: "Point",
        coordinates: [location.longitude, location.latitude],
      },
    })),
  };
}

export function boundsFromMissionHeatmapLocations(
  locations: readonly MissionHeatmapLocationInput[],
): ProfileMapFitBounds | undefined {
  const rankedLocations = ensureRankedHeatmapLocations(locations);
  if (rankedLocations.length === 0) {
    return undefined;
  }

  const latitudes = rankedLocations.map((location) => location.latitude);
  const longitudes = rankedLocations.map((location) => location.longitude);
  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);
  const avgLat = (minLat + maxLat) / 2;
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  const maxRadiusMiles = Math.max(
    ...rankedLocations.map((location) => location.radiusMiles ?? experienceHeatmapMaxRadiusMiles),
  );
  const radiusLatPadding = maxRadiusMiles / milesPerDegreeLatitude;
  const radiusLngPadding = milesToLongitudeDegrees(maxRadiusMiles, avgLat);
  const latPadding = (latSpan > 0 ? Math.max(0.05, latSpan * 0.12) : 0.2) + radiusLatPadding;
  const lngPadding = (lngSpan > 0 ? Math.max(0.05, lngSpan * 0.12) : 0.2) + radiusLngPadding;

  return {
    southWest: [minLng - lngPadding, minLat - latPadding],
    northEast: [maxLng + lngPadding, maxLat + latPadding],
  };
}
